// React-обёртка над двумя статическими словарями (ru/en).
// Без сторонних i18n-либ, чтобы не раздувать бандл — у нас всего 2 локали
// и нет рантайм-загрузки переводов.
//
// API:
//   <I18nProvider initial="ru"> ... </I18nProvider>
//   const t = useT()                  // t('key'), t.n('unread', 5)
//   const [locale, setLocale] = useLocale()
//
// Persist: автоматически пишем выбранную локаль в localStorage по ключу
// LOCALE_STORAGE_KEY. Перезагрузка страницы → подхватим оттуда.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { ru } from './ru';
import { en } from './en';

export type Locale = 'ru' | 'en';
export type DictKey = keyof typeof ru;

const DICTS: Record<Locale, Record<DictKey, string>> = { ru, en };

export const LOCALE_STORAGE_KEY = 'anexica.locale.v1';

function isLocale(s: string | null | undefined): s is Locale {
  return s === 'ru' || s === 'en';
}

export interface DetectInitOpts {
  navigatorLang?: string | null | undefined;
}

export function detectInitialLocale(opts: DetectInitOpts = {}): Locale {
  if (typeof localStorage !== 'undefined') {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isLocale(stored)) return stored;
  }
  // Если navigatorLang передан явно (включая null) — используем его;
  // если undefined — читаем из глобального navigator.
  const nav =
    'navigatorLang' in opts
      ? opts.navigatorLang
      : typeof navigator !== 'undefined'
        ? navigator.language
        : null;
  const short = nav?.toLowerCase().slice(0, 2);
  if (isLocale(short)) return short;
  return 'ru';
}

export type TVars = Record<string, string | number>;

export type TFn = ((key: DictKey, vars?: TVars) => string) & {
  n: (baseKey: 'unread' | 'messages' | 'days', n: number) => string;
};

function interpolate(tmpl: string, vars: TVars | undefined): string {
  if (!vars) return tmpl;
  return tmpl.replace(/\{(\w+)\}/g, (_, k: string) =>
    k in vars ? String(vars[k]) : `{${k}}`,
  );
}

interface I18nCtx {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: TFn;
}

const Ctx = createContext<I18nCtx | null>(null);

export interface I18nProviderProps {
  /** Если не задан, читается из localStorage / navigator. */
  initial?: Locale;
  children: ReactNode;
}

export function I18nProvider({ initial, children }: I18nProviderProps) {
  const [locale, setLocaleState] = useState<Locale>(
    () => initial ?? detectInitialLocale(),
  );

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, l);
    } catch {
      // приватный режим браузера — игнорируем
    }
  }, []);

  // Если initial поменялся (редкий случай — например, после установки в JWT-claim),
  // подхватим его. Для тестов это удобно.
  useEffect(() => {
    if (initial && initial !== locale) {
      setLocaleState(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial]);

  const t = useMemo<TFn>(() => {
    const dict = DICTS[locale];
    const pluralRules = new Intl.PluralRules(locale);
    const base = ((key: DictKey, vars?: TVars) => {
      const tmpl = dict[key] ?? key;
      return interpolate(tmpl, vars);
    }) as TFn;
    base.n = (baseKey, n) => {
      const cat = pluralRules.select(n);
      const key = `${baseKey}.${cat}` as DictKey;
      const tmpl = dict[key] ?? `${baseKey}.${cat}`;
      return interpolate(tmpl, { n });
    };
    return base;
  }, [locale]);

  const value = useMemo<I18nCtx>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useT(): TFn {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useT must be used inside <I18nProvider>');
  return ctx.t;
}

export function useLocale(): [Locale, (l: Locale) => void] {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useLocale must be used inside <I18nProvider>');
  return [ctx.locale, ctx.setLocale];
}
